import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  CircularProgress,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
  Chip,
  Alert
} from "@mui/material";
import DownloadIcon from '@mui/icons-material/Download';
import { useApi } from "../../../services/useApi";
import { ApiSuccessResponse } from "../../../types/api";
import DataTable from "../../../components/common/DataTable";
import OneSnackbar from "../components/OneSnackbar";
import axios from "axios";
import { authService } from "../../../services/auth.service";

interface AccountingYear {
  Name: string;
  FromDate: string;
  ToDate: string;
  SalarySumTaxStateEnum: string;
}

interface DineroContact {
  Name: string;
  ContactGuid: string;
  ExternalReference: string | null;
  IsPerson: string;
  Street: string;
  ZipCode: string;
  City: string;
  CountryKey: string;
  Phone: string | null;
  Email: string | null;
  Webpage: string | null;
  AttPerson: string | null;
  VatNumber: string;
  EanNumber: string | null;
  PaymentConditionType: string;
  PaymentConditionNumberOfDays: string;
  IsMember: string;
  MemberNumber: string | null;
  CompanyStatus: string;
  VatRegionKey: string;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string | null;
}

interface StateOfAccountItem {
  RelatedVoucherGuid: string;
  RelatedVoucherNumber: string;
  Date: string;
  DecayDate: string | null;
  Description: string;
  Type: string;
  Amount: number;
  Balance: number;
  Status: string;
  RelatedVoucherCategory: string;
  RelatedVoucherClass: string;
  Currency: string;
  ContactName: string;
  ContactGuid: string;
}

interface StateOfAccountPanelProps {}

const StateOfAccountPanel: React.FC<StateOfAccountPanelProps> = () => {
  const [selectedContact, setSelectedContact] = useState<DineroContact | null>(null);
  const [contacts, setContacts] = useState<DineroContact[]>([]);
  const [contactsLoading, setContactsLoading] = useState<boolean>(false);
  const [accountingYears, setAccountingYears] = useState<AccountingYear[]>([]);
  const [selectedAccountingYear, setSelectedAccountingYear] = useState<string>("");
  const [accountingYearsLoading, setAccountingYearsLoading] = useState<boolean>(false);
  const [data, setData] = useState<StateOfAccountItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);
  const [error, setError] = useState<string | string[] | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [exportJobId, setExportJobId] = useState<number | null>(null);
  const [exportStatus, setExportStatus] = useState<'pending' | 'processing' | 'completed' | 'failed' | null>(null);
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [exportErrorMessage, setExportErrorMessage] = useState<string | null>(null);
  const [exportFileName, setExportFileName] = useState<string | null>(null);

  const api = useApi();

  useEffect(() => {
    const loadAccountingYears = async () => {
      setAccountingYearsLoading(true);
      try {
        const response = await api.get<ApiSuccessResponse<AccountingYear[]>>(
          "/dinero/accounting-years"
        );
        if (response.data.success) {
          setAccountingYears(response.data.data);
        }
      } catch (error) {
        console.error("Error loading accounting years:", error);
        setError("Failed to load accounting years. Please try again later.");
      } finally {
        setAccountingYearsLoading(false);
      }
    };

    const loadContacts = async () => {
      setContactsLoading(true);
      try {
        const response = await api.get<ApiSuccessResponse<DineroContact[]>>(
          "/dinero/contacts",
          {
            params: {
              all: true
            }
          }
        );
        if (response.data.success) {
          // Remove duplicates based on ContactGuid
          const uniqueContacts = response.data.data.filter((contact, index, self) =>
            index === self.findIndex((c) => c.ContactGuid === contact.ContactGuid)
          );
          setContacts(uniqueContacts);
        }
      } catch (error) {
        console.error("Error loading contacts:", error);
        // Don't show error for contacts loading, just log it
      } finally {
        setContactsLoading(false);
      }
    };

    const loadLatestExportJob = async () => {
      try {
        const response = await api.get<ApiSuccessResponse<any>>(
          "/dinero/state-of-account/export/latest"
        );
        if (response.data.success && response.data.data) {
          const job = response.data.data;
          // Only show if job is pending, processing, or completed (not failed or old completed)
          if (job.status === 'pending' || job.status === 'processing' || job.status === 'completed') {
            setExportJobId(job.id);
            setExportStatus(job.status);
            if (job.file_name) {
              setExportFileName(job.file_name);
            }
            if (job.error_message) {
              setExportErrorMessage(job.error_message);
            }
          }
        }
      } catch (error) {
        // No export job found is fine, just ignore
        console.log("No latest export job found");
      }
    };

    loadAccountingYears();
    loadContacts();
    loadLatestExportJob();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount - api is stable from useApi hook

  // Poll export status if there is an active job
  useEffect(() => {
    if (!exportJobId) {
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        const response = await api.get<ApiSuccessResponse<any>>(
          `/dinero/state-of-account/export/${exportJobId}`
        );
        if (response.data.success) {
          const status = response.data.data.status;
          setExportStatus((prev) => (prev === status ? prev : status));
          if (response.data.data.file_name) {
            setExportFileName(response.data.data.file_name);
          }
          if (status === 'failed') {
            setExportErrorMessage(response.data.data.error_message || 'Export failed');
          }
          if (status === 'completed' || status === 'failed') {
            clearInterval(pollInterval);
          }
        }
      } catch (error) {
        console.error("Error checking export status:", error);
        clearInterval(pollInterval);
      }
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(pollInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportJobId]);

  const handleLoadData = async () => {
    if (!selectedContact) {
      setError("Please select a contact");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const params: any = {
        contact: selectedContact.ContactGuid
      };

      // Only add accounting year dates if selected
      if (selectedAccountingYear) {
        const selectedYear = accountingYears.find(year => year.Name === selectedAccountingYear);
        if (selectedYear) {
          params.from = selectedYear.FromDate;
          params.to = selectedYear.ToDate;
        }
      }

      const response = await api.get<ApiSuccessResponse<any>>(
        "/dinero/state-of-account",
        {
          params: params,
        }
      );

      if (response.data.success) {
        // Handle both array and object responses
        let dataArray: StateOfAccountItem[] = [];
        if (Array.isArray(response.data.data)) {
          dataArray = response.data.data;
        } else if (response.data.data && typeof response.data.data === "object") {
          // If it's an object, convert to array
          dataArray = [response.data.data];
        }

        setData(dataArray);
        setHasLoaded(true);
        setSuccess(`Successfully loaded ${dataArray.length} record(s)`);
      }
    } catch (error: any) {
      console.error("Error loading state of account data:", error);
      if (!error.message?.includes("No connection info")) {
        setError("Failed to load state of account data. Please try again later.");
      }
      setHasLoaded(true); // Still mark as loaded even on error to show empty table
    } finally {
      setLoading(false);
    }
  };


  // Dynamically generate columns from the first data item (raw data, no formatting)
  const generateColumns = () => {
    if (data.length === 0) {
      return [];
    }

    const firstItem = data[0];
    return Object.keys(firstItem).map((key) => ({
      id: key,
      label: key
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase())
        .trim(),
    }));
  };

  const handleExportToExcel = async () => {
    setExportLoading(true);
    setExportErrorMessage(null);
    setError(null);

    try {
      const response = await api.post<ApiSuccessResponse<any>>(
        "/dinero/state-of-account/export",
        {}
      );

      if (response.data.success) {
        setExportJobId(response.data.data.id);
        setExportStatus(response.data.data.status);
        if (response.data.data.file_name) {
          setExportFileName(response.data.data.file_name);
        }
        setSuccess("Export job created successfully. Processing...");
      }
    } catch (error: any) {
      console.error("Error creating export job:", error);
      setError("Failed to create export job. Please try again later.");
    } finally {
      setExportLoading(false);
    }
  };

  const handleDownloadExport = async () => {
    if (!exportJobId) return;

    try {
      // Use axios directly for blob download
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
      const token = authService.getToken();
      
      const response = await axios.get(
        `${baseURL}/dinero/state-of-account/export/${exportJobId}/download`,
        {
          responseType: 'blob',
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
          },
        }
      );

      // Get filename from Content-Disposition header or use exportFileName
      let fileName = exportFileName || `state_of_account_${new Date().toISOString().split('T')[0]}.xlsx`;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1].replace(/['"]/g, '');
        }
      }

      // Create blob and download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setSuccess("File downloaded successfully");
    } catch (error: any) {
      console.error("Error downloading export:", error);
      setError("Failed to download file. Please try again later.");
    }
  };

  const columns = generateColumns();

  return (
    <>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Load State Of Account
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Autocomplete
            size="small"
            sx={{ minWidth: 250 }}
            options={contacts}
            value={selectedContact}
            onChange={(event, newValue) => {
              setSelectedContact(newValue);
              // Reset export state when changing contact so download button doesn't apply to old filters
              setExportJobId(null);
              setExportStatus(null);
              setExportErrorMessage(null);
              setExportFileName(null);
            }}
            getOptionLabel={(option) => {
              if (!option) return "";
              return option.MemberNumber 
                ? `${option.Name} (${option.MemberNumber})` 
                : option.Name;
            }}
            filterOptions={(options, { inputValue }) => {
              const searchValue = inputValue.toLowerCase();
              return options.filter((option) => {
                const nameMatch = option.Name.toLowerCase().includes(searchValue);
                const guidMatch = option.ContactGuid.toLowerCase().includes(searchValue);
                const memberMatch = option.MemberNumber?.toLowerCase().includes(searchValue);
                return nameMatch || guidMatch || memberMatch;
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Contact *"
                placeholder="Search by name or ContactGuid"
                required
              />
            )}
            renderOption={(props, option) => {
              // Extract key from props and ensure it's unique
              const { key, ...otherProps } = props;
              return (
                <Box component="li" {...otherProps} key={option.ContactGuid}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {option.Name}
                    </Typography>
                    {option.MemberNumber && (
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        ({option.MemberNumber})
                      </Typography>
                    )}
                    <Typography variant="caption" sx={{ color: 'text.secondary', ml: 'auto' }}>
                      {option.ContactGuid}
                    </Typography>
                  </Box>
                </Box>
              );
            }}
            loading={contactsLoading}
            disabled={contactsLoading}
            isOptionEqualToValue={(option, value) => option.ContactGuid === value?.ContactGuid}
          />
          <FormControl size="small" sx={{ minWidth: 250 }}>
            <InputLabel>Accounting Year</InputLabel>
            <Select
              value={selectedAccountingYear}
              onChange={(event) => {
                setSelectedAccountingYear(event.target.value);
                // Reset export state when changing accounting year
                setExportJobId(null);
                setExportStatus(null);
                setExportErrorMessage(null);
                setExportFileName(null);
              }}
              label="Accounting Year"
              disabled={accountingYearsLoading}
              renderValue={(value) => {
                if (!value) {
                  return <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>None</Typography>;
                }
                const selectedYear = accountingYears && accountingYears.length > 0 ? accountingYears.find(year => year.Name === value) : null;
                return selectedYear ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'blue' }}>
                      {selectedYear.Name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      ({selectedYear.FromDate} &rarr; {selectedYear.ToDate})
                    </Typography>
                  </Box>
                ) : value;
              }}
            >
              <MenuItem value="">
                <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                  None
                </Typography>
              </MenuItem>
              {accountingYears && accountingYears.length > 0 ? accountingYears.map((year) => (
                <MenuItem key={year.Name} value={year.Name}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'blue' }}>
                      {year.Name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      ({year.FromDate} &rarr; {year.ToDate})
                    </Typography>
                  </Box>
                </MenuItem>
              )) : null}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            onClick={handleLoadData}
            sx={{ minWidth: 120 }}
            disabled={loading || !selectedContact || accountingYearsLoading || contactsLoading}
          >
            {loading ? <CircularProgress size={24} /> : "Load Data"}
          </Button>
          <Button
            variant="outlined"
            onClick={handleExportToExcel}
            sx={{ minWidth: 150 }}
            disabled={exportLoading}
            startIcon={exportLoading ? <CircularProgress size={20} /> : <DownloadIcon />}
          >
            {exportLoading ? "Creating..." : "Export to Excel"}
          </Button>
        </Box>
        {(exportStatus === 'pending' || exportStatus === 'processing' || exportStatus === 'completed' || exportStatus === 'failed') && (
          <Box sx={{ mt: 2 }}>
            <Alert 
              severity={
                exportStatus === 'completed' ? 'success' :
                exportStatus === 'failed' ? 'error' :
                'info'
              }
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Typography variant="body2" sx={{ flex: 1 }}>
                Export Status: 
                <Chip 
                  label={exportStatus?.toUpperCase()} 
                  size="small" 
                  color={
                    exportStatus === 'completed' ? 'success' :
                    exportStatus === 'failed' ? 'error' :
                    'primary'
                  }
                  sx={{ ml: 1 }}
                />
                {exportStatus === 'processing' && (
                  <Box component="span" sx={{ ml: 1, display: 'inline-flex', alignItems: 'center' }}>
                    <CircularProgress size={16} />
                  </Box>
                )}
                {exportStatus === 'completed' && exportFileName && (
                  <Typography variant="caption" sx={{ ml: 2, color: 'text.secondary' }}>
                    File: {exportFileName}
                  </Typography>
                )}
              </Typography>
              {exportStatus === 'completed' && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleDownloadExport}
                  startIcon={<DownloadIcon />}
                >
                  Download
                </Button>
              )}
            </Alert>
            {exportStatus === 'failed' && exportErrorMessage && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                Error: {exportErrorMessage}
              </Typography>
            )}
          </Box>
        )}
      </Paper>

      {hasLoaded && (
        <>
          <Typography
            variant="h5"
            sx={{ mb: 2, fontWeight: "bold", color: "primary.main" }}
          >
            State Of Account Data
          </Typography>
          <DataTable
            columns={columns}
            data={data}
            loading={loading}
            page={0}
            rowsPerPage={data.length || 1}
            totalCount={data.length}
            onPageChange={() => {}}
            onRowsPerPageChange={() => {}}
            maxHeight={600}
            size="small"
            showPagination={false}
          />
        </>
      )}

      <OneSnackbar
        error={error}
        success={success}
        onClose={() => {
          setError(null);
          setSuccess(null);
        }}
      />
    </>
  );
};

export default StateOfAccountPanel;

